<template>
  <template v-if="toolboxGroup">
    <IconPicture size="24" style="padding:10px;" />
    <p>Image</p>
  </template>

  <template v-else>
    <div v-if="elementCustomerButton">
      <FileUpload
          mode="basic"
          name="image[]"
          accept="image/*"
          @select="onUpload($event)"
          :auto="true"
          :chooseLabel="elementCustomerButtonText"
          :style="getStyle()"
      />
    </div>
    <div v-else class="draggable set" @dblclick="editor ? openDialog = !openDialog : null">

      <Dialog v-model:visible="openDialog" modal :style="{ width: '50rem' }" :closable="false">
        <template #header>
          <TabView style="width: 100%">
            <TabPanel header="Photo Browser">
              <FileManager @fileChosen="fileChosen" />
            </TabPanel>
            <TabPanel header="Search Stock Images">
              <StockImageElement :type="`image`" @fileChosen="fileChosen"/>
            </TabPanel>
            <TabPanel header="Search Stock Icons">
              <StockImageElement :type="`icon`" @fileChosen="fileChosen" @filterCss="filterCss"/>
            </TabPanel>
            </TabView>
        </template>

        <template #footer>
          <Button label="Cancel" severity="secondary" @click="openDialog = false" />
        </template>
      </Dialog>

      <div v-if="elementCustomerImageLink">
        <img :style="{ ...getStyle(), ...filterStyle }" :src="elementCustomerImageLink" alt="img" />
      </div>

      <div v-else-if="elementImageUrl" class="image-holder">
        <img :style="{ ...getStyle(), ...filterStyle }" :src="elementImageUrl" alt="img" />
      </div>

      <div v-else>
        <img
            style="max-width: 100%"
            src="https://img.freepik.com/premium-vector/default-image-icon-vector-missing-picture-page-website-design-mobile-app-no-photo-available_87543-11093.jpg"
            alt="img" />
      </div>

    </div>
  </template>
</template>

<script setup lang="ts">
import {computed, toRefs, getCurrentInstance, ref, watch, onMounted, defineEmits} from 'vue'
import FileManager from "@/views/components/element/FileManagerElement/index.vue";
import StockImageElement from "@/views/components/element/StockImageElement/index.vue";
import {useWidgetStore, useMainStore, useCustomerStore, useSlidesStore} from "@/store";
import {storeToRefs} from "pinia";

const props = defineProps({
  elementData: {
    type: Object
  },
  viewer: {
    type: Boolean
  },
  style: {
    type: Object
  },
  class: String
})
const { elementData } = toRefs(props)

const widgetStore = useWidgetStore()
const mainStore = useMainStore()
const customerStore = useCustomerStore()
const slidesStore = useSlidesStore()

const { selectedWidget } = storeToRefs(widgetStore)
const {slideLibraryActive, editor} = storeToRefs(mainStore)
const { valueModelId, theme } = storeToRefs(slidesStore)

const openDialog = ref(false)
const iconDimensions = computed(() => elementData.value?.style?.fontSize)

const elementImageUrl = computed(() =>
  elementData.value?.imageUrl
    ? elementData.value?.imageUrl
      : elementData.value?.idotImageUrl
        ? elementData.value?.idotImageUrl
          : null
)

const fontName = computed(() => `"${theme.value.fontName}"` ?? '')
const elementCustomerButton = computed(() => elementData.value?.customerImageButton?.button ?? false)
const elementCustomerButtonText = computed(() => elementData.value?.customerImageButton?.text ?? 'Choose')
const elementCustomerImageLink = computed(() => {
  const widgetId = elementData.value?.customerImageButtonWidgetId
  if (!widgetId) return false

  const image = customerStore.customerImages.find(image => image.widgetId === widgetId)

  return image?.url || false
})

const elementStyle = computed(() => {
  const style = elementData.value?.style ?? {};
  const { filter, ...withoutFilter } = style;
  return withoutFilter;
});

const filterStyle = computed(() => {
  const style = elementData.value?.style ?? {};
  return {
    filter: style.filter
  };
});

const toolboxGroup = computed(() => {
  const parentClassList = getCurrentInstance().parent.attrs.class;
  return parentClassList === "dragArea toolbox-group"
});

const px = (input, defaultValue) => {
  if (input) {
    return `${input}px`
  }
  else {
    return defaultValue
  }
}

const getStyle = () => {
  return {
    maxWidth: px(elementData.value.style?.imageMaxWidth, '100%'),
    maxHeight: px(elementData.value.style?.imageMaxHeight, '100%'),
    width: px(elementData.value.style?.imageWidth, ''),
    height: px(elementData.value.style?.imageHeight, ''),
    fontSize: elementData.value.style?.fontSize,
    color: elementData.value.style?.color,
    fontWeight: elementData.value.style?.fontWeight,
    fontStyle: elementData.value.style?.fontStyle,
    textDecoration: elementData.value.style?.textDecoration,
    backgroundColor: elementData.value.customerImageButton?.color,
    fontFamily: fontName.value,
    border: 'none'
  }
}
const emit = defineEmits(['idotImageChosen'])
const fileChosen = (file: any) => {
  if (elementData.value.idotImage) {
    emit('idotImageChosen', file.resultsThumbnailImg)
  } else if (file.resultsThumbnailImg) {
    elementData.value.style.filter = ''
    selectedWidget.value.imageUrl = file.resultsThumbnailImg

    const width = file.attributes.width ?? file.width
    const height = file.attributes.height ?? file.height
    if (width && height) {
      selectedWidget.value.style.imageSrcWidth = width
      selectedWidget.value.style.imageSrcHeight = height
      selectedWidget.value.style.imageRatio = width / height
    }

  } else {
    elementData.value.style.filter = ''
    selectedWidget.value.imageUrl = file.url;
    if (file.width && file.height) {
      selectedWidget.value.style.imageSrcWidth = file.width
      selectedWidget.value.style.imageSrcHeight = file.height
      selectedWidget.value.style.imageRatio = file.width / file.height
    }
  }

  openDialog.value = false
}

const filterCss = (obj: any) => {
  elementData.value.style.filter = obj.filter
  elementData.value.style.hex = obj.hex
}

const onUpload = ({ files }) => {
  const file = files[0]
  saveFiles(file)
}

const saveFiles = async (file) => {
  const image = customerStore.customerImages.find(image => image.widgetId === elementData.value.id)
  const {putEndpoint, imageKey} = await getSignedURL(image.uuid, file.name, file.type)
  const response = await fetch(putEndpoint, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error('File upload failed');
  } else {
    customerStore.updateImage(elementData.value.id, { url: `${import.meta.env.VITE_APP_ENV_SERVER}/${imageKey}` })
  }
};

const getSignedURL = async (uuid: string, fileName: string, contentType: string) => {
  const url = `${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/file-service/customer-image`;
  const data = {
    vmId: valueModelId.value,
    uuid: uuid,
    fileName: fileName,
    contentType: contentType
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      console.error("Failed to send data:", response.statusText);
    }

    return await response.json();
  } catch (error) {
    console.error("Error sending data:", error);
    throw new Error('/');
  }
}

onMounted(() => {
  if (elementData.value?.subType === 'customer_image_button') {
    const notes = elementData.value?.customerImageButton.notes ?? null
    customerStore.setUpImage(elementData.value.id, notes)
  }
})

watch(openDialog, (newVal) => {
  if (newVal) {
    slideLibraryActive.value = false;
  }
});

</script>

<style scoped>
.btn-primary {
  border-radius: 3px;
  background-color: #122276;
  border-color: #122276;
  color: white !important;
}

.p-button.p-component.btn.btn-primary > .i-icon {
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #0056b3;
  color: white;
}

.btn-secondary  {
  border-radius: 3px;
}

:deep(.p-tabview-panels) {
  overflow: auto;
  max-height: 400px;
}

:deep(.p-icon.p-button-icon) {
  width: v-bind(iconDimensions);
  height: v-bind(iconDimensions);
}

.section-highlight .image-holder {
  box-shadow: 0 0 0 1px #CCCCCC;
}
</style>
